/**
 * Which products offer campaign inclusions — the one decision point.
 *
 * ## ✅ The rule is DECIDED, and it is the product↔inclusion LINK TABLE
 *
 * The owner: **which products offer campaign inclusions is set per product by an
 * administrator**, through the link table. Not by product name, not by category.
 * `PUT /products/{id}/inclusions { inclusionIds }` is where the decision is
 * recorded and `GET /products/{id}/inclusions` is where it is read;
 * `ProductInclusionsModal`, mounted from the "Inclusions" row action on
 * `/products`, is the screen an admin does it on.
 *
 * **A product offers campaign inclusions when an admin has linked at least one
 * to it.** That is the whole rule, and it is one line of code:
 * {@link resolveInclusionsEligibility} step 1.
 *
 * ### The table decides WHICH, not only WHETHER
 *
 * The same rows answer both questions and the owner has decided they must answer
 * both: {@link offeredInclusionIds} is the option list a picker may show, and it is
 * the *same* `inclusionIds` the gate counts. The reasoning is the one this whole
 * feature turns on — **there is no server-side backstop.** Writing `inclusions` on
 * a line takes any active inclusion id, so an unnarrowed picker lets someone tick
 * something the administrator never linked to that product, it saves cleanly, and
 * it **prints on the customer's Xero quote**. Nothing downstream catches it; the
 * picker is the check.
 *
 * ⚠️ Narrowing is a rule about what may be **added**, never about what is already
 * **there** — see {@link offeredInclusionIds}, and see the destruction warning
 * below, which a filter is a very easy way to violate silently.
 *
 * ### ⚠️ What this REPLACED, and why the replacement is the point
 *
 * Until now this module matched `/master ?class/i` against the product **NAME**,
 * on the owner's earlier and much looser instruction (*"campaign inclusions are
 * to be given only when it is for a masterclass product."*). That was recorded
 * here at length as **KNOWN-BAD**, and the reason is worth keeping now that it is
 * gone, because it is the standard against which the new rule should be judged:
 *
 * > Someone renames `Virtual Masterclasses` to `Virtual Master Class Series` — an
 * > ordinary catalogue edit, made by an admin who has never heard of this file —
 * > and the inclusions block silently stops appearing. Nothing throws, nothing
 * > logs, `tsc` is green, the page renders perfectly, and the person who can no
 * > longer add `Campus Outreach` has no reason to connect it to a rename that
 * > happened three weeks ago.
 *
 * The link table is not merely a better signal, it is a **different kind of
 * fact**. A display name is a coincidence this module was exploiting; a link row
 * is somebody's recorded decision, and it survives every rename, every bulk
 * backfill and every catalogue tidy-up. Renaming a product now changes what the
 * product is called and nothing else.
 *
 * A `productCategory` branch also stood here and never once fired — `ProductSummary`
 * does not declare the field and every product picker mapped it away. It is gone
 * with the name match. The request to carry the category on `ProductSummary` had
 * already been **withdrawn**, on the backend's argument that a free-text,
 * admin-typed category fails in exactly the same way a name does, one rename
 * later. They were right, and the table is what they built instead. Do not
 * reintroduce either signal as a fallback — see the empty-table section, which is
 * the one place the temptation is real.
 *
 * ## ⚠️ The table is EMPTY, and that is the INTENDED state
 *
 * Measured on the wire: `GET /products/1/inclusions` →
 * `200 {"data":[]}`. Product 1 is `Virtual Masterclasses`, the single name match
 * in the catalogue and therefore the one product the old rule allowed. It offers
 * nothing.
 *
 * The backend **seeded nothing, deliberately**: which products offer which
 * inclusions is a business decision nobody has taken, and a guessed seed is
 * indistinguishable from a real decision the day after it lands. So **every
 * product answers `'no'` until an admin says otherwise**, and every line in the
 * app hides its inclusions editor. That is this module working, not this module
 * broken.
 *
 * ⚠️ **Do NOT add a fallback to the name match for an empty set.** It is the
 * obvious-looking repair and it would resurrect the retired rule *invisibly* —
 * the app would keep behaving exactly as it did, an admin's deliberate "this
 * product offers none" would be overridden by a coincidence of spelling, and
 * nothing would report it. The empty table is a decision waiting to be entered,
 * not a gap waiting to be filled in by a client.
 *
 * ⚠️ **An empty set is a real answer and is NOT the same as an unread one.** They
 * are separate inputs here for exactly that reason — see {@link ProductInclusionOffer}
 * versus `null`. Collapsing them is the `a ?? b` failure this repo catalogues, and
 * it would be invisible: `[]` and "we could not ask" both look like nothing.
 *
 * ## The signal order
 *
 * {@link resolveInclusionsEligibility} consults, in order:
 *
 * | # | input | answer | block |
 * | - | ----- | ------ | ----- |
 * | 0 | {@link NO_PRODUCT_CHOSEN} | `'not-chosen'` | hidden, **nothing said** |
 * | 1 | a {@link ProductInclusionOffer} — the link table answered | `'yes'` / `'no'` | shows / hidden with a sentence |
 * | 2 | `null` or `undefined` — a product is in play and the offer is unread | `'unknown'` | **shows** |
 *
 * Step 0 is not a signal read off a product — it is the *absence* of a product,
 * said explicitly rather than inferred from a `null`. See the section on it below;
 * that distinction is the one this file got wrong once.
 *
 * ## An unread offer is not a "no"
 *
 * While the request is in flight, or it failed outright, the honest answer is "we
 * cannot tell yet". {@link resolveInclusionsEligibility} says so with
 * `answer: 'unknown'`, and {@link productOffersInclusions} resolves that to
 * **`true`** — show the block.
 *
 * That direction is chosen, not defaulted. Hiding a feature on a load failure is
 * unrecoverable from the user's side: they see a screen with no control on it, no
 * error, and nothing to retry — the failure is indistinguishable from the feature
 * not existing, and from an admin having decided against it, which is now a real
 * and common state. Showing it costs a person the chance to tick something the
 * admin did not link.
 *
 * ⚠️ **Be honest about what that costs HERE.** The usual justification in this
 * repo — *"showing it costs at worst a
 * refusal from the server that names the real problem"* — **does not apply to
 * this feature.** The link table governs what a picker OFFERS, never what a
 * document may record: writing `inclusions` on a line takes any active inclusion
 * id, so there is no refusal waiting to catch it. And the owner has decided
 * inclusions **print in the line description on the customer's Xero quote**, so
 * the thing at the end of this path is a customer document.
 *
 * The direction still stands, and the reason it stands is that the two failures
 * are not symmetric: hiding is silent and unattributable, while showing puts the
 * choice in front of a person who is deliberately ticking a box. But a call site
 * resolving `'unknown'` should **say the check did not run** rather than
 * presenting the editor as though it had. `QuoteLineItemsTable` does.
 *
 * ## ⚠️ …but NO PRODUCT CHOSEN is not "unknown" either, and conflating them shipped
 *
 * This section is the correction to the one above, and the paragraph above used
 * to contain the bug: it named *"the picker is empty, the request is in flight,
 * the read failed"* in one sentence — **three situations, two of which are
 * uncertainty and one of which is not.**
 *
 * The owner reported it from a screenshot of the New Quote modal: a brand-new,
 * empty line row — Item reading *"Choose a product"*, the row flagged *"1 row is
 * not ready"* — with the full **Campaign inclusions** panel rendered underneath
 * it. *"the campaign inclusions should show up after the relevant product is
 * selected and not by default visible."*
 *
 * The mechanism: the quote grid returned a bare `null` both for *no item chosen
 * at all* and for *an item is chosen and cannot be resolved*, both landed on
 * `'unknown'`, and `'unknown'` resolves towards showing. **One `null` carrying
 * two meanings** — the same failure shape this repo catalogues under `a ?? b`,
 * `cityIds: []` vs an omitted key, and `xeroQuoteId === null`.
 *
 * So there is a fourth answer, `'not-chosen'`, reached only through the explicit
 * {@link NO_PRODUCT_CHOSEN} sentinel:
 *
 * - **"we cannot tell" is uncertainty** — a state we might be wrong about, so we
 *   fail towards being useful and show the block.
 * - **"no product has been chosen" is a FACT the client holds with certainty**,
 *   and it is not a gap that a retry would close. There is nothing for inclusions
 *   to be offered *on*. Nothing is being denied — the question has not been asked.
 *
 * ⚠️ **The fix was to separate the two inputs, NOT to change what `'unknown'`
 * does.** Making `'unknown'` resolve to hide would have fixed the screenshot and
 * reintroduced the exact failure argued against above. `'unknown'` still shows.
 * Do not "simplify" these two back together — and note the move to the link table
 * did not touch either of them.
 *
 * ## ⚠️ NEVER DESTROY INCLUSIONS THAT ALREADY EXIST
 *
 * The load-bearing rule of the file, and the **empty table makes it matter more,
 * not less**. Every product now answers `'no'`, so every line that holds
 * inclusions is a line whose editor is hidden — the exact configuration in which
 * a careless clear destroys data.
 *
 * An inclusions write **REPLACES the whole set**, so a form that hides the block
 * *and clears its draft state* will send `inclusions: []` on the next unrelated
 * save and delete them, with a 200 and no warning.
 *
 * Two rules, and they are not the same rule:
 *
 * 1. **Hiding the block must NEVER reset the draft.** If the editor is not
 *    rendered, the line's stored inclusions must stay in state untouched so the
 *    "did the set change" test stays false and the key is omitted from the patch.
 *    An omitted key leaves them alone; `[]` destroys them. Do not clear state when
 *    you stop rendering a control. **This one is absolute** — it holds under
 *    either gate below, and violating it loses customer-visible data.
 * 2. **Whether to keep the editor on such a line is a PRODUCT decision**, and both
 *    answers ship in this file:
 *    - {@link shouldShowInclusionsEditor} — `offers OR already holds some`. The
 *      lenient rule.
 *    - {@link productOffersInclusions} — the strict rule. Consistent, at the cost
 *      of leaving an existing set visible-but-read-only.
 *
 *    ⚠️ **The quote grid deliberately uses the STRICT one**, on the owner's
 *    explicit decision. See {@link shouldShowInclusionsEditor}, which records what
 *    the move to the link table does and does not change about that reasoning.
 *
 * ## Pure, and one decision point
 *
 * No hooks, no fetching, no React — a pure function over data the caller already
 * holds, like `inclusions.ts` and `line-project.ts`. **The link table is fetched,
 * and it is fetched by the CALLER**; this module takes the answer, never the
 * request. That is what keeps one rule readable by a grid that resolves many
 * products at once and by a modal that resolves one, without either of them
 * re-deriving it.
 */

/**
 * What an administrator has linked to one product.
 *
 * ⚠️ **`inclusionIds: []` is a REAL ANSWER — "this product offers none" — and is
 * not the same fact as having no offer at all.** That is the whole reason this is
 * an object rather than a bare array with `null` for both: today the table is
 * empty, so `[]` is what every product returns, and a shape that let `[]` and
 * "unread" wear the same value would silently turn every load failure into a
 * decision. Constructing this object is a statement that the read SUCCEEDED.
 *
 * `readonly` because nothing here may sort, filter or mutate it: the caller owns
 * the fetched set and this module only ever counts it.
 */
export interface ProductInclusionOffer {
  /**
   * The campaign-inclusion ids this product offers, exactly as
   * `GET /products/{id}/inclusions` returned them.
   *
   * Empty is the normal case today and permanently the normal case for most
   * products — the rule is opt-in per product, so a catalogue where a handful of
   * products offer inclusions and the rest offer none is the steady state, not a
   * half-configured one.
   */
  inclusionIds: readonly number[];
}

/**
 * The state a caller is in when there is no product to ask about yet.
 *
 * ⚠️ **Pass this, never `null`, when the item box is empty.** The two are not
 * interchangeable and the whole point of this constant is that they cannot be
 * confused: `null` here means *"we could not read this product's offer"* — in
 * flight, or failed — and resolves to `'unknown'` and therefore to **showing** the
 * block. This means *"there is no product"*, resolves to `'not-chosen'`, and hides
 * it.
 *
 * A `unique symbol` rather than a sentinel object on purpose. A frozen marker
 * object anyone might reach for would have to be distinguished by inspecting its
 * contents, and `{ inclusionIds: [] }` is already a meaningful value. A symbol
 * cannot collide with any offer shape, cannot be produced by accident, and narrows
 * cleanly with `===`.
 *
 * It is also why this is not "a second implicit sentinel": it has a name, it is
 * imported, and it reads at the call site as the sentence it means. The bug being
 * fixed was one `null` silently carrying two meanings; replacing it with two
 * unnamed nullish values would be the same bug.
 */
export const NO_PRODUCT_CHOSEN: unique symbol = Symbol('product-inclusions:no product chosen');

/** The type of {@link NO_PRODUCT_CHOSEN}, for a call site that needs to name it. */
export type NoProductChosen = typeof NO_PRODUCT_CHOSEN;

/**
 * Everything a call site might be holding, as one input type.
 *
 * `null` and `undefined` belong to the *unresolved* half, and that is the
 * distinction the whole file turns on: they mean *a product is in play and we
 * could not read its offer*, which is uncertainty and shows the block. Not
 * knowing and there being nothing to know about are different facts, so they are
 * different types.
 */
export type InclusionsOfferInput = ProductInclusionOffer | NoProductChosen | null | undefined;

/**
 * Four answers, and no two of them collapse.
 *
 * `'unknown'` is a first-class member and the house pattern — the same choice
 * `line-project.ts` makes with `kind: 'unknown'`. Collapsing it into `'no'` would
 * turn "we could not tell" into a decision, which is the shape of failure this
 * repo keeps finding: nothing errors, and the missing thing looks intentional.
 *
 * ⚠️ **`'not-chosen'` was split OUT of `'unknown'`, and the argument above is
 * exactly why it had to be.** This type said *"Three answers, not two"* and made
 * the case for `'unknown'` as uncertainty — while an empty item box was being
 * answered `'unknown'` too, so a state the client knows for certain was being
 * reported as one it could not determine. The point survives and now applies
 * twice: **do not collapse an answer into a neighbour that means something else**,
 * in either direction.
 *
 * | | means | block |
 * | --- | --- | --- |
 * | `'yes'` | an admin linked at least one inclusion to this product | shows |
 * | `'no'` | an admin has linked none | hidden, with {@link inclusionsDenialReason} |
 * | `'unknown'` | a product is in play and its offer is unread | **shows** — fail towards useful |
 * | `'not-chosen'` | there is no product | hidden, and **nothing is said** |
 */
export type InclusionsAnswer = 'yes' | 'no' | 'unknown' | 'not-chosen';

/**
 * Which value produced the answer.
 *
 * ⚠️ **`'category'` and `'name'` are GONE**, with the branches that emitted them.
 * A caller that was switching on `'name'` to warn about the known-bad signal has
 * nothing left to warn about — that is the change, not an omission.
 *
 * `'none'` accompanies both `'unknown'` and `'not-chosen'`: in neither case was an
 * offer consulted. It is deliberately not split further — the two answers already
 * carry the distinction between "there was nothing to read" and "there was nothing
 * to read it off". Read {@link InclusionsEligibility.answer} for that, never the
 * signal.
 */
export type InclusionsSignal = 'link-table' | 'none';

export interface InclusionsEligibility {
  answer: InclusionsAnswer;
  /**
   * The signal that decided it. One real value now, and it is kept rather than
   * dropped for two reasons: a dev tooltip or bug report can still say *how* the
   * answer was reached, and a second signal landing later (a per-product flag, a
   * category that finally means something) is then an addition rather than a
   * change of shape at every call site.
   */
  signal: InclusionsSignal;
  /**
   * How many inclusions the admin linked to this product; `null` when no offer was
   * consulted at all.
   *
   * ⚠️ **`0` and `null` are different facts and must not be coalesced.** `0` means
   * the read succeeded and the answer was none — today's answer for every product.
   * `null` means nothing was read. A `?? 0` here turns every load failure into an
   * admin decision, which is the exact substitution this module's input type
   * exists to prevent.
   */
  offeredCount: number | null;
  /**
   * **WHICH** inclusions the admin linked — the option list a picker may offer.
   *
   * ⚠️ **The empty set and `null` carry the same distinction {@link offeredCount}
   * does, in the shape a picker consumes**, and it is the one that must survive to
   * the call site: an **empty set** is *an administrator linked none*, and `null`
   * is *no offer was consulted* (`'unknown'` or `'not-chosen'`). A picker handed
   * `null` must **not narrow at all** — narrowing on an unread offer produces an
   * empty option list indistinguishable from an admin's deliberate "none", which
   * is this module's central failure mode wearing a new hat. A `?? new Set()` here
   * is the same bug as `?? 0` one field up.
   *
   * It lives on the resolver rather than in a separate reader so that *which* and
   * *whether* can never disagree: the count is this set's size on every branch,
   * because both are produced in the same `return`.
   *
   * `ReadonlySet` because a caller may only ask it questions. The set is
   * constructed fresh per call and has no stable identity — key a `useMemo` on the
   * offer, never on this.
   */
  offeredIds: ReadonlySet<number> | null;
}

/**
 * **THE decision point.** Everything else in this file reads this.
 *
 * Returns the evidence alongside the answer so a caller never has to re-inspect
 * the offer to explain itself.
 */
export function resolveInclusionsEligibility(
  offer: InclusionsOfferInput,
): InclusionsEligibility {
  /*
   * 0. There is no product. Not "we cannot tell" — see the header.
   *
   * This must be first: it is the one input that is a statement rather than a
   * value to read. It is also the only branch reachable by a symbol, which is what
   * keeps the property access below from ever having to think about one.
   *
   * ⚠️ `offeredCount` is `null` because nothing was consulted — not because the
   * product offers none. A caller must not read it as a fact about a catalogue
   * row; read `answer` instead.
   */
  if (offer === NO_PRODUCT_CHOSEN) {
    return { answer: 'not-chosen', signal: 'none', offeredCount: null, offeredIds: null };
  }

  /*
   * 1. The link table — the rule, and now the ONLY thing that decides anything.
   *
   * A product offers campaign inclusions when an admin has linked at least one.
   * `[]` is a decision and answers `'no'`; it is today's answer for all 28
   * products, because the backend seeded the table with nothing on purpose.
   *
   * ⚠️ No fallback belongs here. An empty set previously fell through to a product
   * NAME match, and reinstating that would override an admin's recorded "none"
   * with a coincidence of spelling, silently. See the header.
   */
  if (offer !== null && offer !== undefined) {
    /*
     * ⚠️ The set is built from the SAME array the count is taken from, in this one
     * `return`, so "how many" and "which" cannot drift. Duplicates in the response
     * would collapse here — which is the correct answer for an option list and is
     * why the count is `size`, not `length`: two rows naming one inclusion offer
     * one inclusion.
     */
    const offeredIds: ReadonlySet<number> = new Set(offer.inclusionIds);
    return {
      answer: offeredIds.size > 0 ? 'yes' : 'no',
      signal: 'link-table',
      offeredCount: offeredIds.size,
      offeredIds,
    };
  }

  /*
   * 2. A product IS in play and its offer could not be read.
   *
   * ⚠️ **This must not also cover "no product selected yet."** That is step 0
   * above and never reaches here — conflating the two once meant a brand-new
   * empty line row rendered the whole inclusions panel.
   *
   * What is left is uncertainty and only uncertainty: the request is in flight, or
   * `GET /products/{id}/inclusions` failed. Not a `'no'`, and not a `'not-chosen'`
   * — this resolves towards SHOWING the block, deliberately, and the header states
   * plainly what that costs on this feature.
   */
  return { answer: 'unknown', signal: 'none', offeredCount: null, offeredIds: null };
}

/**
 * **The option list a picker may offer**, or `null` meaning *do not narrow*.
 *
 * The owner's decision, and the reason this exists: the link table already decides
 * **whether** a product offers campaign inclusions; it must also decide **which**.
 * An unnarrowed picker lets someone tick an inclusion an administrator never
 * linked to that product, **and it prints on the customer's Xero quote** — there
 * is no server-side refusal behind it, because the table governs what a picker
 * offers and never what a document may record.
 *
 * ⚠️ **`null` means SHOW EVERYTHING, and it is not a "no options" answer.** It is
 * returned for `'unknown'` and `'not-chosen'` alike — no offer was consulted — and
 * a caller that narrowed on it would turn a request in flight, or a failed read,
 * into an empty picker that looks exactly like an administrator having linked
 * none. That is the same substitution {@link InclusionsEligibility.offeredCount}'s
 * `0`-versus-`null` warning is about, and it is worse here because the empty
 * picker is what a person acts on. `'unknown'` still shows the block, unnarrowed,
 * and {@link productOffersInclusions} is unchanged.
 *
 * ⚠️ **An EMPTY set is a real answer and must narrow to nothing** — but a caller
 * never sees it in a rendered picker, because an empty set answers `'no'` and the
 * block is hidden with {@link inclusionsDenialReason} instead. The empty set is
 * reachable here only if a caller asks this question without asking the gate
 * first, which is why it is spelled out rather than assumed away.
 *
 * ⚠️ **Narrowing an option list is NOT a licence to drop a stored entry.** An
 * inclusion already on the line whose id is absent from this set must be rendered
 * and labelled, never filtered into silence: an inclusions write REPLACES the
 * whole set, so a picker that hid it would send an array without it on the next
 * save and destroy it with a 200. That entry is also NOT retired — the master row
 * is perfectly active, it is simply not linked to this product — and saying
 * "retired" about it would be a false statement about a master row. See
 * `QuoteLineItemsTable`'s `InclusionsEditor`, which keeps the two in separate
 * blocks with separate wording.
 */
export function offeredInclusionIds(offer: InclusionsOfferInput): ReadonlySet<number> | null {
  return resolveInclusionsEligibility(offer).offeredIds;
}

/**
 * Does this product offer campaign inclusions?
 *
 * **`'unknown'` resolves to `true`**, deliberately: an offer still in flight or
 * failed outright must not read as a decision to hide the feature. See the header,
 * and see {@link OFFERS_BY_ANSWER} for all four verdicts.
 *
 * ⚠️ **`false` does not imply a refusal.** {@link NO_PRODUCT_CHOSEN} answers
 * `false` here too, and nothing is being denied on that row — there is no product
 * for anything to be offered on. A call site that renders a sentence off `false`
 * must take it from {@link inclusionsDenialReason}, which is `null` in that state,
 * rather than writing its own.
 *
 * ## ⚠️ Which of the two gates to call is a decision, and it is TAKEN
 *
 * Both remain true, so both are stated:
 *
 * - **{@link shouldShowInclusionsEditor} is the more conservative of the two.** A
 *   saved line may already hold inclusions on a product that answers `false`, and
 *   hiding the editor there leaves data the customer can see on the document and
 *   the user cannot change.
 * - **The quote grid calls THIS function**, on the owner's explicit decision.
 *   Consistency of the rule was preferred to editability of the exception.
 *   `QuoteLineItemsTable`'s `RowInclusions.offers` is that call site; it is not an
 *   oversight and must not be "corrected" to the lenient one.
 *
 * ⚠️ Either way, {@link hasOrphanedInclusions} still has to be consulted — under
 * the strict gate to *say* the line holds inclusions it can no longer edit, and
 * under both gates to make sure hiding the block never clears the draft and sends
 * `inclusions: []`. See the header; that half is not a decision, it is data loss.
 */
export function productOffersInclusions(offer: InclusionsOfferInput): boolean {
  return OFFERS_BY_ANSWER[resolveInclusionsEligibility(offer).answer];
}

/*
 * Below rather than above only so the exported function reads first; it is a
 * module-level const and is initialised before anything can call through it.
 */
/**
 * Every answer's verdict, spelled out — the one place the four map to two.
 *
 * ⚠️ **A `Record` rather than `answer !== 'no'`, and that is the point of it.**
 * The old expression made *showing* the default for anything that was not a flat
 * refusal, so `'not-chosen'` was shown the moment it existed without a line of
 * code being written about it — and a fifth answer would inherit the same
 * unexamined `true`. Keyed on {@link InclusionsAnswer}, `tsc` refuses to compile a
 * new member until somebody states its verdict here.
 *
 * The two `true`s are not the same decision and should not be read as one:
 * `'yes'` is the rule saying so, `'unknown'` is the deliberate fail-towards-useful
 * direction argued at length in the header. `'not-chosen'` is `false` because
 * there is no product for anything to be offered on — it is not a refusal, which
 * is why {@link inclusionsDenialReason} says nothing about it.
 */
const OFFERS_BY_ANSWER: Record<InclusionsAnswer, boolean> = {
  yes: true,
  unknown: true,
  no: false,
  'not-chosen': false,
};

/**
 * ⛔ **DELETED 11 Sep 2026 — `inclusionsRequiredOnLine` and
 * `INCLUSION_REQUIRED_MESSAGE` are GONE, and this note is here so the next
 * reader does not rebuild them.**
 *
 * *SUPERSEDED: "**Must a line selling this product name at least one campaign
 * inclusion?** The owner's rule, applied by all three line grids: a line whose
 * product **offers** inclusions has to have one selected."*
 *
 * That rule predated per-inclusion requiredness. With no way to say WHICH
 * inclusion mattered, "at least one" was the only thing a client could ask for.
 * Migration 159 lets an admin mark each offer required, and the owner chose
 * **pre-tick, still removable** — so a refusal contradicts the removability that
 * was the point of the choice.
 *
 * ⚠️ **Nothing on the server ever backed it up, and nothing does now.** A line
 * with no inclusions is a clean `2xx` on all three endpoints. Enforcement was
 * offered to the owner alongside the flag and turned down, for a reason recorded
 * in migration 159's header: this app hides the inclusions block on lines whose
 * product is not gated for it, so an enforced refusal could name a control the
 * person cannot see and cannot reach — the shape `PROJECT_HAS_NO_CITIES` already
 * has, where the advice cannot be followed from the screen showing the error.
 *
 * ⚠️ **So do not reinstate either symbol.** A client-side refusal with no server
 * refusal behind it is a convention dressed as a contract, which is the mistake
 * this file warns about in three other places. What `required` buys is a
 * PRE-TICK and a label, and that is all it may ever buy without a fresh decision.
 *
 * ⚠️ **The `'unknown'`-splits-two-ways reasoning below is NOT dead and must not
 * be deleted with the gate it justified.** {@link productOffersInclusions}
 * resolves an unread offer towards SHOWING the editor, and any future predicate
 * that could COST somebody a save must resolve it the other way. That asymmetry
 * is the transferable half — it is why one boolean could not serve both then and
 * why a pre-tick keyed on an unread offer must pre-tick nothing.
 */

/*
 * ── the surviving reasoning, kept from the deleted gate's own doc ─────────────
 *
 * The two predicates agreed on three of the four answers and disagreed on
 * `'unknown'` — a FAILED OR IN-FLIGHT READ. `GET /products/{id}/inclusions` not
 * coming back is not evidence that a product offers inclusions, so nothing that
 * costs a person their work may key on it. Showing an editor on an unread offer
 * costs a click; refusing a save on one cost the save.
 *
 * ⚠️ **The `REQUIRED_BY_ANSWER` `Record` went too, and its own warning is worth
 * carrying forward to whatever replaces it**: a `Record` keyed on
 * {@link InclusionsAnswer} was used rather than an `answer === 'yes'` expression
 * so that a fifth answer would not compile until somebody stated its verdict.
 * Any future per-answer decision here should be a `Record` for the same reason.
 */

/**
 * Why the inclusions block is absent, for the UI to say out loud.
 *
 * `null` whenever the block is being shown — including for `'unknown'`, where
 * there is no denial to explain because nothing was denied.
 *
 * ⚠️ **A control that vanishes with no sentence beside it reads as a bug**, and on
 * this feature it reads as a specific one: the person adding a line knows
 * inclusions exist, because they have seen them on another line. Render this where
 * the block would have been.
 *
 * ## ⚠️ Why the sentence is worded the way it is
 *
 * The first sentence is **unchanged from the name-match era, and that is the
 * strongest evidence for how it was written.** It read *"Campaign inclusions are
 * not currently offered on this product."* precisely so it would report an
 * OUTCOME rather than explain a rule — chosen when the rule was unconfirmed, and
 * argued at the time on the grounds that the backend intended a product↔inclusion
 * link table which might make any rule-shaped wording a lie. The rule changed
 * underneath it and the sentence is still exactly true. Copy that describes the
 * screen survives a contract change; copy that explains a rule does not.
 *
 * An earlier draft read *"Campaign inclusions are recorded on masterclass products
 * only"*, which would today be a flat falsehood printed in the UI.
 *
 * - **`currently`** is doing real work: an admin can change this on the product,
 *   so the claim is scoped to now rather than stated as a property of the product.
 * - **It does not read as an error.** A product that offers no inclusions is the
 *   normal case — every product, today — so no "cannot", no "not permitted",
 *   nothing that sends someone hunting a fault.
 *
 * ⚠️ **The second sentence is NEW, and it is licensed by the rule now being
 * decided.** This module previously forbade any copy naming the rule, because the
 * rule was a guess. It is not a guess any more: an administrator sets this per
 * product, on the product, and saying so turns a dead end into something the
 * reader can act on or ask about. It names no category and no product class — the
 * two things that were unconfirmed then and are still not this module's to state.
 *
 * A call site wanting more than this should render it **beside** the product it is
 * about, not reword it.
 */
export function inclusionsDenialReason(offer: InclusionsOfferInput): string | null {
  /*
   * ⚠️ **`'not-chosen'` returns `null` — no sentence at all — and that is a
   * DECISION, not the `!== 'no'` test happening to cover it.**
   *
   * On a row with no item chosen there IS no product, so every reading of the
   * sentence below is false: it asserts something about a catalogue row that does
   * not exist, on a row already telling the person to choose one. It is precisely
   * the confidently-wrong copy CLAUDE.md's "copy is the one part of a contract that
   * nothing typechecks" is about — a wrong sentence ships, renders perfectly, and
   * is believed. Nobody would file it, either: it reads as the app knowing
   * something.
   *
   * Nothing renders instead, and that is the right silence rather than the usual
   * hazard. The header's warning about a vanishing control is about a control
   * *taken away* from someone who has seen it work. Here it has not appeared yet,
   * and the row's own "Choose a product." already says what to do next.
   *
   * The three genuinely-denied answers are unchanged: `'no'` gets the sentence,
   * `'yes'` and `'unknown'` show the editor and have nothing to explain.
   */
  if (resolveInclusionsEligibility(offer).answer !== 'no') return null;
  return (
    'Campaign inclusions are not currently offered on this product. ' +
    'An administrator chooses which products offer them, on the product itself.'
  );
}

/**
 * A line that already HOLDS inclusions on a product that does not offer them.
 *
 * ⚠️ **Not a defensive case — the COMMON one, today.** The link table is empty, so
 * every product answers `'no'`; any line anywhere in the system that carries an
 * inclusion is this. Lines predate the rule and are entirely unaffected by it:
 * the table governs what a picker OFFERS, never what a document already records,
 * and the backend's own migration says so.
 *
 * `inclusionCount` is `line.inclusions.length` — the read is always present and
 * `[]` when empty, so no `?? []` is needed at the call site.
 *
 * ⚠️ **`true` obliges the caller to SAY SOMETHING, and never to destroy the set.**
 * What it does not decide is whether the editor renders — that is the screen's
 * choice between the lenient and strict gates, and the quote grid took the strict
 * one. Under either, a block that is not rendered must leave the stored inclusions
 * in state, or the next save sends `inclusions: []` and deletes them with a 200.
 *
 * Whichever way, it is worth a sentence beside the line: inclusions that show on
 * the customer's document while the product no longer offers them read as the rule
 * not working, and the reader has no other way to tell.
 */
export function hasOrphanedInclusions(
  offer: InclusionsOfferInput,
  inclusionCount: number,
): boolean {
  /*
   * ⚠️ **Keyed on `answer === 'no'`, NOT on `!productOffersInclusions()`**, and
   * the two differ on exactly one input: {@link NO_PRODUCT_CHOSEN}.
   *
   * An orphan is a set stranded *by a product's rule* — the product does not
   * qualify, so the editor is gone and the entries are stuck. With no product
   * there is no rule and no verdict, so there is nothing to be stranded by, and
   * calling it one would send the caller to render the read-only block: a heading
   * reading "on this line, and read-only" over copy about "this item", on a row
   * with no item. False on both counts.
   *
   * The reachable case is someone choosing an offering product, ticking
   * inclusions, then clearing the item box. **Nothing is lost.** The draft is
   * untouched (hiding a block never writes state), `updatePayload` omits the key
   * so a stored set is preserved, `createPayload` omits it on a row that cannot be
   * saved anyway — `validateRow` refuses it with "Choose a product." — and
   * re-picking an offering item brings the entries straight back.
   */
  return inclusionCount > 0 && resolveInclusionsEligibility(offer).answer === 'no';
}

/**
 * **The LENIENT gate.** Show the inclusions block?
 *
 * `offers OR already holds some` — one call, so a screen that wants existing
 * inclusions to stay editable cannot forget the orphan half. That omission is
 * invisible in review (`if (productOffersInclusions(p))` looks completely
 * correct), which is what earns a function of its own.
 *
 * ⚠️ **This is NOT what the quote grid calls, and the move to the link table did
 * not change that** — the decision was the owner's and reversing it is theirs
 * too. What the move DOES change is the argument on both sides, so the reasoning
 * is re-stated rather than left to be re-derived from a stale premise:
 *
 * - **The case for the STRICT gate got stronger.** Under the name match a `'no'`
 *   could be an accident — a rename nobody connected to this feature — so hiding
 *   an editor on that basis was hiding it on a coincidence. A `'no'` is now an
 *   administrator's recorded decision about that product. Enforcing it
 *   consistently is enforcing something somebody actually said.
 * - **The case for the LENIENT gate is unchanged in kind and much larger in
 *   scope.** It was ever about not stranding a set that prints on a customer's
 *   document. With the table empty, *every* product answers `'no'`, so the lenient
 *   gate would keep the editor on every line in the system that holds an
 *   inclusion, while the strict gate hides it on all of them. The difference
 *   between the two is no longer an edge case.
 *
 * Neither of those is a reason to switch unilaterally, and switching would reverse
 * a decision rather than repair a bug. It is a reason to **put the second point to
 * the owner**, now that "a handful of pre-existing exceptions" has become "every
 * line until the table is populated".
 *
 * Pair it with {@link inclusionsDenialReason} for the case where this is false.
 *
 * ⚠️ **Still correct under {@link NO_PRODUCT_CHOSEN}.** An empty row holds nothing,
 * so both halves are false and the editor stays away — the behaviour the owner
 * asked for, under this gate as well as the strict one. A row that had entries
 * ticked and then had its item cleared answers `true` here, which is the lenient
 * rule being consistent with itself: keep the control while the data is there, so
 * the person can undo it. That is a different answer from
 * {@link hasOrphanedInclusions}, deliberately — this one offers an editor, that
 * one describes a stranded set, and only the second would put a sentence about
 * "this item" on a row with no item.
 */
export function shouldShowInclusionsEditor(
  offer: InclusionsOfferInput,
  inclusionCount: number,
): boolean {
  return productOffersInclusions(offer) || inclusionCount > 0;
}
